import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { slugify } from '@utils/slugify';
import { parseYoutubeId, youtubeIdToUrl } from '@utils/youtube';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import { useTagOptions } from '@features/tags/hooks';
import type { TagOption } from '@features/tags/schema';
import { ImageUploadField } from './cells/ImageUploadField';
import {
  NEWS_ARTICLE_KINDS,
  NEWS_ARTICLE_STATUSES,
  newsArticleFormSchema,
  type NewsArticle,
  type NewsArticleDetail,
  type NewsArticleFormTranslation,
  type NewsArticleFormValues,
} from '../schema';
import {
  useCreateNewsArticle,
  useNewsArticle,
  useUpdateNewsArticle,
} from '../hooks';

interface Props {
  open: boolean;
  article: NewsArticle | null;
  onClose: () => void;
}

const KIND_LABELS: Record<(typeof NEWS_ARTICLE_KINDS)[number], string> = {
  article: 'Article (text)',
  video: 'Video (YouTube)',
};
const STATUS_LABELS: Record<(typeof NEWS_ARTICLE_STATUSES)[number], string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

function buildFormTranslations(
  locales: Locale[],
  existing: NewsArticleDetail['translations'] | undefined,
): NewsArticleFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      title: byCode.get(locale.code)?.title ?? '',
      summary: byCode.get(locale.code)?.summary ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: NewsArticleDetail | null,
): NewsArticleFormValues {
  return {
    kind: existing?.kind ?? 'article',
    slug: existing?.slug ?? '',
    title: existing?.title ?? '',
    summary: existing?.summary ?? '',
    status: existing?.status ?? 'draft',
    imageStorageKey: existing?.imageStorageKey ?? null,
    thumbnailStorageKey: existing?.thumbnailStorageKey ?? null,
    youtubeVideoId: existing?.youtubeVideoId ?? null,
    translations: buildFormTranslations(locales, existing?.translations),
    tagIds: existing?.tags.map((t) => t.id) ?? [],
  };
}

export function NewsArticleFormDialog({ open, article, onClose }: Props) {
  const isEditing = !!article;
  const createMutation = useCreateNewsArticle();
  const updateMutation = useUpdateNewsArticle();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const { data: tagOptions = [] } = useTagOptions();
  const detailQuery = useNewsArticle(article?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  // Surface any currently-attached tags that have since been deactivated — they
  // should still render as chips (so the editor can intentionally remove them)
  // but the active list governs what's pickable from the dropdown.
  const tagOptionsWithAttached = useMemo<TagOption[]>(() => {
    if (!detail?.tags?.length) return tagOptions;
    const byId = new Map(tagOptions.map((o) => [o.id, o]));
    for (const t of detail.tags) {
      if (!byId.has(t.id)) byId.set(t.id, { id: t.id, code: t.code, name: t.name });
    }
    return Array.from(byId.values());
  }, [tagOptions, detail]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewsArticleFormValues>({
    resolver: zodResolver(newsArticleFormSchema) as Resolver<NewsArticleFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  // Form state stores the 11-char ID; the input field shows the full URL.
  const [youtubeUrlInput, setYoutubeUrlInput] = useState('');
  const [youtubeUrlError, setYoutubeUrlError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
    setYoutubeUrlInput(youtubeIdToUrl(detail?.youtubeVideoId ?? null));
    setYoutubeUrlError(null);
  }, [open, dataReady, locales, detail, isEditing, reset]);

  const kind = watch('kind');
  const title = watch('title');
  const slug = watch('slug');
  const translations = watch('translations');
  const selectedTagIds = watch('tagIds');

  // Map ids → option objects for the Autocomplete value (it works in objects).
  const selectedTags = useMemo<TagOption[]>(() => {
    const byId = new Map(tagOptionsWithAttached.map((o) => [o.id, o]));
    return selectedTagIds
      .map((id) => byId.get(id))
      .filter((o): o is TagOption => !!o);
  }, [selectedTagIds, tagOptionsWithAttached]);

  // Active-only set governs which dropdown rows are pickable; deactivated tags
  // already attached can still be unselected via their chip.
  const activeTagIdSet = useMemo(
    () => new Set(tagOptions.map((o) => o.id)),
    [tagOptions],
  );

  // Slug is immutable post-create — a title rename must not silently change a published URL.
  useEffect(() => {
    if (isEditing) return;
    setValue('slug', slugify(title), { shouldValidate: false, shouldDirty: true });
  }, [title, isEditing, setValue]);

  useEffect(() => {
    if (kind === 'article') {
      setValue('youtubeVideoId', null, { shouldValidate: false, shouldDirty: false });
      setYoutubeUrlInput('');
      setYoutubeUrlError(null);
    }
    if (kind === 'video') {
      // Videos use the YouTube auto-thumbnail; uploaded images aren't kept.
      setValue('imageStorageKey', null, { shouldValidate: false, shouldDirty: false });
      setValue('thumbnailStorageKey', null, { shouldValidate: false, shouldDirty: false });
    }
  }, [kind, setValue]);

  const handleYoutubeUrlChange = (raw: string) => {
    setYoutubeUrlInput(raw);
    if (!raw.trim()) {
      setValue('youtubeVideoId', null, { shouldValidate: false });
      setYoutubeUrlError(null);
      return;
    }
    const id = parseYoutubeId(raw);
    if (id) {
      setValue('youtubeVideoId', id, { shouldValidate: false });
      setYoutubeUrlError(null);
    } else {
      setValue('youtubeVideoId', null, { shouldValidate: false });
      setYoutubeUrlError("That doesn't look like a YouTube URL.");
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    if (isEditing && article) {
      await updateMutation.mutateAsync({ id: article.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit news article' : 'New news article'}</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="news-article-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <Controller
              control={control}
              name="kind"
              render={({ field }) => (
                <TextField {...field} select label="Kind" size="small" fullWidth>
                  {NEWS_ARTICLE_KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {KIND_LABELS[k]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <TextField
              label="Title"
              size="small"
              fullWidth
              error={!!errors.title}
              helperText={
                errors.title?.message ?? (
                  <span className="text-foreground-muted">
                    URL handle:{' '}
                    <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-foreground">
                      {slug || '(start typing a title…)'}
                    </code>
                  </span>
                )
              }
              {...register('title')}
            />

            <TextField
              label="Summary"
              size="small"
              fullWidth
              multiline
              minRows={3}
              error={!!errors.summary}
              helperText={errors.summary?.message ?? ' '}
              {...register('summary')}
            />

            {kind === 'video' && (
              <TextField
                label="YouTube URL"
                size="small"
                fullWidth
                placeholder="Paste the full YouTube link (e.g. https://www.youtube.com/watch?v=…)"
                value={youtubeUrlInput}
                onChange={(e) => handleYoutubeUrlChange(e.target.value)}
                error={!!youtubeUrlError || !!errors.youtubeVideoId}
                helperText={
                  youtubeUrlError ??
                  errors.youtubeVideoId?.message ??
                  'Paste any YouTube link — the share URL, embed URL, or shorts URL all work.'
                }
              />
            )}

            {kind === 'article' && (
              <Controller
                control={control}
                name="imageStorageKey"
                render={({ field }) => (
                  <div>
                    <label className="mb-1.5 block text-[12.5px] font-medium text-foreground">
                      Image
                    </label>
                    <ImageUploadField
                      folder="news"
                      imageStorageKey={field.value}
                      thumbnailStorageKey={watch('thumbnailStorageKey')}
                      onChange={(next) => {
                        setValue('imageStorageKey', next.imageStorageKey, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue(
                          'thumbnailStorageKey',
                          next.thumbnailStorageKey,
                          { shouldValidate: false, shouldDirty: true },
                        );
                      }}
                      error={errors.imageStorageKey?.message}
                    />
                  </div>
                )}
              />
            )}

            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <TextField {...field} select label="Status" size="small" fullWidth>
                  {NEWS_ARTICLE_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Autocomplete<TagOption, true>
              multiple
              size="small"
              options={tagOptions}
              value={selectedTags}
              onChange={(_, opts) =>
                setValue('tagIds', opts.map((o) => o.id), {
                  shouldValidate: false,
                  shouldDirty: true,
                })
              }
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, v) => opt.id === v.id}
              filterSelectedOptions
              noOptionsText={
                tagOptions.length === 0
                  ? 'No active tags — create one in the Tags screen'
                  : 'No matches'
              }
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => {
                  const isInactive = !activeTagIdSet.has(opt.id);
                  // Spread the per-chip props (already includes a unique key).
                  const tagProps = getTagProps({ index });
                  return (
                    <Chip
                      {...tagProps}
                      label={
                        isInactive ? (
                          <span className="flex items-center gap-1">
                            {opt.name}
                            <span className="text-[10px] uppercase tracking-wider text-warning">
                              inactive
                            </span>
                          </span>
                        ) : (
                          opt.name
                        )
                      }
                      size="small"
                      variant={isInactive ? 'outlined' : 'filled'}
                    />
                  );
                })
              }
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Tags"
                  helperText="Optional · pick from existing tags. Manage the vocabulary in the Tags screen."
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-4">
                <div className="-mb-2 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const titleErr = errors.translations?.[i]?.title?.message;
                  const summaryErr = errors.translations?.[i]?.summary?.message;
                  return (
                    <div
                      key={t.localeCode}
                      className="grid gap-3 rounded-lg border border-border bg-surface-muted/40 p-3"
                    >
                      <div className="text-[12px] font-semibold text-foreground">
                        {t.localeName}{' '}
                        <span className="font-mono text-[11px] text-foreground-muted">
                          ({t.localeCode})
                        </span>
                      </div>
                      <TextField
                        label={`Title (${t.localeName})`}
                        size="small"
                        fullWidth
                        error={!!titleErr}
                        helperText={titleErr ?? ' '}
                        {...register(`translations.${i}.title` as const)}
                      />
                      <TextField
                        label={`Summary (${t.localeName})`}
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        error={!!summaryErr}
                        helperText={summaryErr ?? ' '}
                        {...register(`translations.${i}.summary` as const)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="news-article-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
